import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronRight,
  Upload,
  Pencil,
  Plus,
  Trash2,
  GripVertical,
  FileText,
  Images,
  ImageIcon,
  LibraryBig,
  Bold,
  Italic,
  RefreshCw,
  X,
  Loader2,
  Link2,
} from 'lucide-react';

import { useProject, useCreateProject, useUpdateProject } from '@/hooks/useProjects';
import { projectsApi } from '@/services/api/projects';
import { getErrorMessage } from '@/utils/error';
import { resolveImageUrl } from '@/utils/url';
import type { ContentBlockDTO, ContentBlockType, TextStyle, TechnicalSpecDTO } from '@/types/project';

export function CreateProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = id ? Number(id) : undefined;
  const isEditMode = !!projectId;

  const { data: existingProject, isLoading: isLoadingProject } = useProject(projectId);
  const createProject = useCreateProject();
  const updateProject = useUpdateProject(projectId ?? 0);

  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [roleSpec, setRoleSpec] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [androidLink, setAndroidLink] = useState('');
  const [iosLink, setIosLink] = useState('');
  const [webLink, setWebLink] = useState('');

  const [specs, setSpecs] = useState<TechnicalSpecDTO[]>([
    { key: '', value: '' },
  ]);
  const [blocks, setBlocks] = useState<ContentBlockDTO[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [showThumbnailUrlInput, setShowThumbnailUrlInput] = useState(false);
  const [thumbnailUrlInput, setThumbnailUrlInput] = useState('');

  useEffect(() => {
    if (existingProject) {
      setName(existingProject.name);
      setShortDescription(existingProject.shortDescription || '');
      setRoleSpec(existingProject.roleSpecification || '');
      setIsLive(existingProject.isLive);
      setTags(existingProject.tags ?? []);
      setAndroidLink(existingProject.androidLink || '');
      setIosLink(existingProject.iosLink || '');
      setWebLink(existingProject.webLink || '');
      setSpecs(
        existingProject.technicalSpecs.length > 0
          ? existingProject.technicalSpecs
          : [{ key: '', value: '' }],
      );
      setBlocks(existingProject.contentBlocks);
      if (existingProject.thumbnailUrl) {
        setThumbnailPreview(resolveImageUrl(existingProject.thumbnailUrl));
        setThumbnailUrl(existingProject.thumbnailUrl);
      }
    }
  }, [existingProject]);

  const handleSave = async () => {
    try {
      setSaveError(null);
      const payload = {
        name,
        shortDescription: shortDescription || undefined,
        roleSpecification: roleSpec || undefined,
        isLive,
        tags: tags.length > 0 ? tags : undefined,
        androidLink: androidLink || undefined,
        iosLink: iosLink || undefined,
        webLink: webLink || undefined,
        thumbnailUrl: thumbnailUrl || undefined,
        technicalSpecs: specs.filter((s) => s.key.trim()),
        contentBlocks: blocks,
      };

      if (isEditMode) {
        await updateProject.mutateAsync(payload);
      } else {
        await createProject.mutateAsync(payload);
      }
      navigate('/dashboard/projects');
    } catch (err) {
      setSaveError(getErrorMessage(err));
    }
  };

  const handleThumbnailSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setThumbnailPreview(previewUrl);
    setThumbnailUploading(true);
    try {
      const url = await projectsApi.uploadImage(file);
      setThumbnailUrl(url);
    } catch (err) {
      setSaveError(getErrorMessage(err));
    } finally {
      setThumbnailUploading(false);
    }

    e.target.value = '';
  };

  const isSaving = createProject.isPending || updateProject.isPending;

  const addSpec = () => setSpecs((prev) => [...prev, { key: '', value: '' }]);
  const removeSpec = (index: number) => setSpecs((prev) => prev.filter((_, i) => i !== index));
  const updateSpec = (index: number, field: 'key' | 'value', val: string) =>
    setSpecs((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: val } : s)));

  const addBlock = (type: ContentBlockType) => {
    setBlocks((prev) => [
      ...prev,
      {
        type,
        order: prev.length,
        heading: '',
        body: '',
        textStyle: 'P' as TextStyle,
        bold: false,
        italic: false,
        imageUrls: [],
        caption: '',
      },
    ]);
  };

  const removeBlock = (index: number) =>
    setBlocks((prev) => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i })));

  const updateBlock = (index: number, changes: Partial<ContentBlockDTO>) =>
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...changes } : b)));

  if (isEditMode && isLoadingProject) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-dark-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pb-16 pt-8 md:px-12">
      {/* Breadcrumb / Header */}
      <div className="mb-12">
        <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-dark-secondary">
          <button onClick={() => navigate('/dashboard/projects')} className="hover:text-white">
            Projects
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-dark-on-surface">
            {isEditMode ? 'Edit Project' : 'Create Project'}
          </span>
        </div>
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <h1 className="font-headline text-4xl font-extrabold tracking-tighter text-dark-on-surface md:text-5xl">
              {isEditMode ? name || 'Edit Project' : 'New Project'}
            </h1>
            <p className="mt-2 max-w-xl text-dark-on-surface-variant">
              Configuration protocol for the high-performance architectural rendering
              engine. Adjust blocks to redefine the project narrative.
            </p>
          </div>
          <div className="flex items-center gap-4">
            {saveError && <span className="text-sm text-error">{saveError}</span>}
            <button
              onClick={() => navigate('/dashboard/projects')}
              className="rounded-full border border-dark-outline-variant px-6 py-2 font-medium text-dark-on-surface transition-all hover:bg-dark-surface-container"
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className="rounded-full bg-gradient-to-br from-dark-primary-container to-dark-secondary-container px-8 py-2 font-bold text-white shadow-[0_40px_40px_-10px_rgba(0,26,64,0.4)] transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-12 xl:grid-cols-12">
        {/* Left Column */}
        <div className="space-y-8 xl:col-span-4">
          {/* Project Avatar */}
          <section className="rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-8">
            <label className="mb-6 block text-[10px] uppercase tracking-widest text-dark-outline">
              Identity Anchor
            </label>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleThumbnailSelect}
            />
            <div className="flex flex-col items-center">
              <div
                className="group relative cursor-pointer"
                onClick={() => thumbnailInputRef.current?.click()}
              >
                <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-xl border border-dark-outline-variant/30 bg-dark-surface-container-highest">
                  {thumbnailPreview ? (
                    <img
                      src={thumbnailPreview}
                      alt="Project thumbnail"
                      className="h-full w-full object-cover"
                    />
                  ) : thumbnailUploading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-dark-outline" />
                  ) : (
                    <Upload className="h-8 w-8 text-dark-outline transition-opacity group-hover:opacity-100" />
                  )}
                </div>
                <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-dark-surface bg-dark-secondary-container text-white shadow-lg">
                  {thumbnailUploading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Pencil className="h-3 w-3" />
                  )}
                </div>
              </div>
              <p className="mt-6 text-center text-xs text-dark-outline">
                Recommended size: 512x512px. PNG or WebP format preferred for blueprint
                clarity.
              </p>
              <button
                onClick={() => setShowThumbnailUrlInput((prev) => !prev)}
                className="mt-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-dark-secondary transition-colors hover:text-dark-primary"
              >
                <Link2 className="h-3 w-3" />
                {showThumbnailUrlInput ? 'Cancel' : 'Or paste image URL'}
              </button>
              {showThumbnailUrlInput && (
                <div className="mt-2 flex w-full gap-2">
                  <input
                    type="url"
                    value={thumbnailUrlInput}
                    onChange={(e) => setThumbnailUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && thumbnailUrlInput.trim()) {
                        e.preventDefault();
                        setThumbnailUrl(thumbnailUrlInput.trim());
                        setThumbnailPreview(thumbnailUrlInput.trim());
                        setThumbnailUrlInput('');
                        setShowThumbnailUrlInput(false);
                      }
                    }}
                    placeholder="https://example.com/image.png"
                    className="flex-1 rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      if (thumbnailUrlInput.trim()) {
                        setThumbnailUrl(thumbnailUrlInput.trim());
                        setThumbnailPreview(thumbnailUrlInput.trim());
                        setThumbnailUrlInput('');
                        setShowThumbnailUrlInput(false);
                      }
                    }}
                    disabled={!thumbnailUrlInput.trim()}
                    className="rounded-lg bg-dark-secondary-container px-3 py-2 text-[10px] font-bold text-white transition-all hover:opacity-80 disabled:opacity-50"
                  >
                    Set
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Configuration Block */}
          <section className="space-y-6 rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-8">
            <label className="mb-2 block text-[10px] uppercase tracking-widest text-dark-outline">
              Technical Metadata
            </label>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  Project Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter project name..."
                  className="w-full rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  Role / Specification
                </label>
                <input
                  type="text"
                  value={roleSpec}
                  onChange={(e) => setRoleSpec(e.target.value)}
                  placeholder="Lead Technical Designer"
                  className="w-full rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  Short Description
                </label>
                <textarea
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  placeholder="A brief description of the project..."
                  rows={3}
                  maxLength={500}
                  className="w-full resize-none rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
                <span className="mt-1 block text-right text-[10px] text-dark-outline">
                  {shortDescription.length}/500
                </span>
              </div>
            </div>
            <div className="border-t border-dark-outline-variant/10 pt-4">
              <label className="group flex cursor-pointer items-center gap-3">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isLive}
                    onChange={(e) => setIsLive(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-5 w-10 rounded-full bg-dark-surface-container-highest transition-colors peer-checked:bg-dark-primary" />
                  <div className="absolute left-1 top-1 h-3 w-3 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
                <span className="text-sm font-medium text-dark-on-surface transition-colors group-hover:text-dark-primary">
                  View Live Project
                </span>
              </label>
            </div>
          </section>

          {/* Tags */}
          <section className="rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-8">
            <label className="mb-4 block text-[10px] uppercase tracking-widest text-dark-outline">
              Tags
            </label>
            <div className="mb-3 flex flex-wrap gap-2">
              {tags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-md bg-dark-surface-container-highest px-3 py-1 text-xs font-bold text-dark-secondary"
                >
                  {tag}
                  <button
                    onClick={() => setTags((prev) => prev.filter((_, idx) => idx !== i))}
                    className="ml-1 text-dark-outline transition-colors hover:text-error"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    e.preventDefault();
                    if (!tags.includes(tagInput.trim())) {
                      setTags((prev) => [...prev, tagInput.trim()]);
                    }
                    setTagInput('');
                  }
                }}
                placeholder="Type a tag and press Enter..."
                className="flex-1 rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-2 text-xs text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
              />
              <button
                onClick={() => {
                  if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                    setTags((prev) => [...prev, tagInput.trim()]);
                  }
                  setTagInput('');
                }}
                className="flex items-center gap-1 rounded-lg bg-dark-surface-container-highest px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-dark-primary hover:opacity-80"
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
          </section>

          {/* Project Links */}
          <section className="space-y-6 rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-8">
            <label className="mb-2 block text-[10px] uppercase tracking-widest text-dark-outline">
              Project Links
            </label>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  Google Play Link
                </label>
                <input
                  type="url"
                  value={androidLink}
                  onChange={(e) => setAndroidLink(e.target.value)}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  className="w-full rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  App Store Link
                </label>
                <input
                  type="url"
                  value={iosLink}
                  onChange={(e) => setIosLink(e.target.value)}
                  placeholder="https://apps.apple.com/app/..."
                  className="w-full rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-dark-on-surface-variant">
                  Web Link
                </label>
                <input
                  type="url"
                  value={webLink}
                  onChange={(e) => setWebLink(e.target.value)}
                  placeholder="https://your-project.com"
                  className="w-full rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-4 py-3 text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                />
              </div>
            </div>
          </section>

          {/* Technical Specs */}
          <section className="rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low p-8">
            <div className="mb-6 flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-widest text-dark-outline">
                TECHNICAL SPECS
              </label>
              <button
                onClick={addSpec}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-dark-primary hover:opacity-80"
              >
                <Plus className="h-4 w-4" /> Row
              </button>
            </div>
            <div className="space-y-3">
              {specs.map((spec, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={spec.key}
                    onChange={(e) => updateSpec(index, 'key', e.target.value)}
                    placeholder="Key"
                    className="w-1/3 rounded-md border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface"
                  />
                  <input
                    type="text"
                    value={spec.value}
                    onChange={(e) => updateSpec(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1 rounded-md border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface"
                  />
                  <button
                    onClick={() => removeSpec(index)}
                    className="px-1 text-dark-outline-variant transition-colors hover:text-error"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column: Block-based Content */}
        <div className="space-y-6 xl:col-span-8">
          <div className="flex items-center justify-between px-2">
            <h2 className="font-headline text-xl font-bold tracking-tight">
              Narrative Infrastructure
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => addBlock('TEXT')}
                className="rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container p-2 text-dark-outline transition-colors hover:bg-dark-surface-container-highest"
                title="Add Text Block"
              >
                <FileText className="h-5 w-5" />
              </button>
              <button
                onClick={() => addBlock('IMAGE_SLIDER')}
                className="rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container p-2 text-dark-outline transition-colors hover:bg-dark-surface-container-highest"
                title="Add Image Slider"
              >
                <Images className="h-5 w-5" />
              </button>
              <button
                onClick={() => addBlock('HERO_IMAGE')}
                className="rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container p-2 text-dark-outline transition-colors hover:bg-dark-surface-container-highest"
                title="Add Single Image"
              >
                <ImageIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {blocks.map((block, index) => (
            <ContentBlockEditor
              key={index}
              block={block}
              index={index}
              onUpdate={(changes) => updateBlock(index, changes)}
              onRemove={() => removeBlock(index)}
            />
          ))}

          {/* Empty State / Add Block */}
          {blocks.length === 0 && (
            <div className="group flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-dark-outline-variant/20 py-12 transition-colors hover:border-dark-primary/40">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-dark-surface-container text-dark-outline transition-colors group-hover:text-dark-primary">
                <LibraryBig className="h-8 w-8" />
              </div>
              <div className="text-center">
                <p className="font-headline text-lg font-bold">Append System Block</p>
                <p className="text-xs text-dark-outline">
                  Select a block type above to extend the project&apos;s technical
                  documentation.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContentBlockEditor({
  block,
  index,
  onUpdate,
  onRemove,
}: {
  block: ContentBlockDTO;
  index: number;
  onUpdate: (changes: Partial<ContentBlockDTO>) => void;
  onRemove: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);

  const blockLabel =
    block.type === 'TEXT'
      ? `Text Block #${index + 1}`
      : block.type === 'IMAGE_SLIDER'
        ? `Multi-Image Slider #${index + 1}`
        : `Featured Hero Image #${index + 1}`;

  const currentStyle: TextStyle = block.textStyle || 'P';
  const styles: TextStyle[] = ['H1', 'H2', 'H3', 'H4', 'P'];

  const textSizeClass = {
    H1: 'font-headline text-4xl font-extrabold tracking-tight',
    H2: 'font-headline text-3xl font-bold tracking-tight',
    H3: 'font-headline text-2xl font-bold',
    H4: 'font-headline text-xl font-semibold',
    P: 'text-base',
  }[currentStyle];

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    setUploading(true);
    try {
      if (block.type === 'HERO_IMAGE') {
        const url = await projectsApi.uploadImage(files[0]);
        onUpdate({ imageUrls: [url] });
      } else {
        // IMAGE_SLIDER: upload all selected files
        const urls = await projectsApi.uploadImages(files);
        onUpdate({ imageUrls: [...(block.imageUrls || []), ...urls] });
      }
    } catch {
      // silently fail for now
    } finally {
      setUploading(false);
    }

    e.target.value = '';
  };

  return (
    <article className="group relative overflow-hidden rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container">
      <div className="absolute bottom-0 left-0 top-0 w-1 bg-dark-primary-container opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="flex items-center justify-between border-b border-dark-outline-variant/10 bg-dark-surface-container-high p-4">
        <div className="flex items-center gap-4">
          <GripVertical className="h-5 w-5 cursor-move text-dark-outline" />
          <span className="text-[10px] uppercase tracking-widest text-dark-secondary">
            {blockLabel}
          </span>
        </div>
        <button
          onClick={onRemove}
          className="p-1 text-dark-outline transition-colors hover:text-error"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="p-8">
        {block.type === 'TEXT' && (
          <>
            {/* Text style selector + bold/italic toggles */}
            <div className="mb-6 flex w-fit items-center gap-2 rounded-lg bg-dark-surface-container-lowest p-1">
              {styles.map((style) => (
                <button
                  key={style}
                  onClick={() => onUpdate({ textStyle: style })}
                  className={`rounded px-3 py-1 text-[10px] font-bold uppercase transition-colors ${
                    currentStyle === style
                      ? 'bg-dark-surface-container-highest text-white'
                      : 'text-dark-outline hover:bg-dark-surface-container-highest'
                  }`}
                >
                  {style}
                </button>
              ))}
              <div className="mx-1 my-auto h-4 w-px bg-dark-outline-variant/30" />
              <button
                onClick={() => onUpdate({ bold: !block.bold })}
                className={`rounded p-1 transition-colors ${
                  block.bold
                    ? 'bg-dark-primary/20 text-dark-primary'
                    : 'text-dark-outline hover:bg-dark-surface-container-highest'
                }`}
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                onClick={() => onUpdate({ italic: !block.italic })}
                className={`rounded p-1 transition-colors ${
                  block.italic
                    ? 'bg-dark-primary/20 text-dark-primary'
                    : 'text-dark-outline hover:bg-dark-surface-container-highest'
                }`}
              >
                <Italic className="h-4 w-4" />
              </button>
            </div>
            <textarea
              value={block.body || ''}
              onChange={(e) => onUpdate({ body: e.target.value })}
              placeholder={
                currentStyle === 'P'
                  ? 'Write your paragraph here...'
                  : `Enter ${currentStyle} heading...`
              }
              rows={currentStyle === 'P' ? 5 : 2}
              className={`w-full resize-none border-none bg-transparent text-dark-on-surface placeholder:text-dark-outline-variant focus:ring-0 ${textSizeClass} ${block.bold ? 'font-bold' : ''} ${block.italic ? 'italic' : ''}`}
            />
          </>
        )}

        {block.type === 'IMAGE_SLIDER' && (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="hidden"
                onChange={handleImageSelect}
              />
              {(block.imageUrls || []).map((url, i) => (
                <div
                  key={i}
                  className="group/img relative aspect-square overflow-hidden rounded-lg border border-dark-outline-variant/30 bg-dark-surface-container-highest"
                >
                  <img
                    src={resolveImageUrl(url) || url}
                    alt={`Slide ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                  <button
                    onClick={() =>
                      onUpdate({
                        imageUrls: (block.imageUrls || []).filter((_, idx) => idx !== i),
                      })
                    }
                    className="absolute right-2 top-2 rounded-full bg-error/90 p-1 text-white opacity-0 transition-opacity group-hover/img:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex aspect-square flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-dark-outline-variant/30 text-dark-outline transition-all hover:border-dark-primary-container hover:text-dark-primary"
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <>
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      Upload
                    </span>
                  </>
                )}
              </button>
            </div>
            <div className="mt-4">
              <button
                onClick={() => setShowUrlInput((prev) => !prev)}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-dark-secondary transition-colors hover:text-dark-primary"
              >
                <Link2 className="h-3 w-3" />
                {showUrlInput ? 'Cancel' : 'Or paste image URL'}
              </button>
              {showUrlInput && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && imageUrlInput.trim()) {
                        e.preventDefault();
                        onUpdate({ imageUrls: [...(block.imageUrls || []), imageUrlInput.trim()] });
                        setImageUrlInput('');
                      }
                    }}
                    placeholder="https://example.com/image.png"
                    className="flex-1 rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      if (imageUrlInput.trim()) {
                        onUpdate({ imageUrls: [...(block.imageUrls || []), imageUrlInput.trim()] });
                        setImageUrlInput('');
                      }
                    }}
                    disabled={!imageUrlInput.trim()}
                    className="rounded-lg bg-dark-secondary-container px-3 py-2 text-[10px] font-bold text-white transition-all hover:opacity-80 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {block.type === 'HERO_IMAGE' && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleImageSelect}
            />
            <div className="group/hero relative flex aspect-video flex-col items-center justify-center overflow-hidden rounded-xl border border-dark-outline-variant/20 bg-dark-surface-container-lowest">
              {block.imageUrls?.[0] ? (
                <img
                  src={resolveImageUrl(block.imageUrls[0]) || block.imageUrls[0]}
                  alt="Hero"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-dark-outline">
                  Hero Image Placeholder
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-dark-surface-container-lowest/60 opacity-0 transition-opacity group-hover/hero:opacity-100">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 rounded-full bg-white px-6 py-2 text-sm font-bold text-dark-surface shadow-xl transition-transform hover:scale-105"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {block.imageUrls?.[0] ? 'Replace High-Res Image' : 'Upload Image'}
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={block.caption || ''}
                onChange={(e) => onUpdate({ caption: e.target.value })}
                placeholder="Image caption or alt text..."
                className="w-full border-b border-dark-outline-variant/20 bg-transparent py-2 text-xs text-dark-on-surface-variant transition-colors focus:border-dark-primary focus:outline-none"
              />
              <button
                onClick={() => setShowUrlInput((prev) => !prev)}
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-dark-secondary transition-colors hover:text-dark-primary"
              >
                <Link2 className="h-3 w-3" />
                {showUrlInput ? 'Cancel' : 'Or paste image URL'}
              </button>
              {showUrlInput && (
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && imageUrlInput.trim()) {
                        e.preventDefault();
                        onUpdate({ imageUrls: [imageUrlInput.trim()] });
                        setImageUrlInput('');
                        setShowUrlInput(false);
                      }
                    }}
                    placeholder="https://example.com/hero-image.png"
                    className="flex-1 rounded-lg border border-dark-outline-variant/20 bg-dark-surface-container-lowest px-3 py-2 text-xs text-dark-on-surface transition-colors focus:border-dark-primary focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      if (imageUrlInput.trim()) {
                        onUpdate({ imageUrls: [imageUrlInput.trim()] });
                        setImageUrlInput('');
                        setShowUrlInput(false);
                      }
                    }}
                    disabled={!imageUrlInput.trim()}
                    className="rounded-lg bg-dark-secondary-container px-3 py-2 text-[10px] font-bold text-white transition-all hover:opacity-80 disabled:opacity-50"
                  >
                    Set
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  );
}
